// Vocal-Tuning: Tonhöhe erkennen (YIN), in Noten zerlegen, Korrekturkurve rechnen, Noten auf die Tonart ziehen.
// Reine Funktionen (auch im Worker nutzbar). Zeiten in Quell-Sekunden des Clips, Tonhöhen als MIDI-Zahl mit Nachkommastellen.
import { inKey } from '@srs/shared';

export const PITCH_HOP = 0.01;
export interface PitchTrack { /** Grundfrequenz je 10 ms in Hz, 0 = stimmlos/still */ f0: Float32Array; /** Sicherheit 0–1 */ conf: Float32Array; hop: number; /** Zeit der Mitte von Rahmen 0 (s) */ off: number }
export const midiOf = (hz: number) => 69 + 12 * Math.log2(hz / 440);
export const hzOf = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

/**
 * YIN auf ein auf ~8 kHz heruntergerechnetes Mono-Signal, Rahmen 32 ms, alle 10 ms.
 * Zwei Durchgänge: erst 60–1100 Hz, dann im Bereich der erkannten Stimmlage (wie „Input Type“ bei Auto-Tune),
 * das verhindert Oktavfehler bei tiefen und hohen Stimmen.
 */
export function yin(x: Float32Array, sr: number): PitchTrack {
  const first = yinPass(x, sr, 60, 1100);
  const v = Array.from(first.f0).filter((f) => f > 0).sort((a, b) => a - b);
  if (v.length < 50) return first;
  const med = v[Math.floor(v.length / 2)];
  return yinPass(x, sr, Math.max(55, med / 2.2), Math.min(1100, med * 2.6));
}
function yinPass(x: Float32Array, sr: number, fLo: number, fHi: number): PitchTrack {
  const fac = Math.max(1, Math.round(sr / 8000)), dsr = sr / fac, n = Math.floor(x.length / fac);
  const y = new Float32Array(n);
  for (let i = 0; i < n; i++) { let s = 0; for (let k = 0; k < fac; k++) s += x[i * fac + k]; y[i] = s / fac; }
  const W = Math.round(Math.max(0.032, 2.2 / fLo) * dsr), tMax = Math.floor(dsr / fLo), tMin = Math.max(2, Math.floor(dsr / fHi)), hop = Math.round(PITCH_HOP * dsr);
  const frames = Math.max(0, Math.floor((n - W - tMax) / hop) + 1);
  const f0 = new Float32Array(frames), conf = new Float32Array(frames), rms = new Float32Array(frames);
  const d = new Float32Array(tMax + 2);
  let peak = 0;
  for (let k = 0; k < frames; k++) { const a = k * hop; let e = 0; for (let i = a; i < a + W; i++) e += y[i] * y[i]; rms[k] = Math.sqrt(e / W); if (rms[k] > peak) peak = rms[k]; }
  const gate = peak * Math.pow(10, -45 / 20);
  for (let k = 0; k < frames; k++) {
    if (rms[k] < gate || rms[k] < 1e-5) continue;
    const a = k * hop;
    let run = 0; d[0] = 1;
    for (let t = 1; t <= tMax + 1; t++) {
      let s = 0; for (let i = a; i < a + W; i++) { const v = y[i] - y[i + t]; s += v * v; }
      run += s; d[t] = run > 0 ? (s * t) / run : 1;
    }
    let tau = -1;
    for (let t = tMin; t <= tMax; t++) if (d[t] < 0.2) { while (t + 1 <= tMax && d[t + 1] < d[t]) t++; tau = t; break; }
    if (tau < 0) { let best = tMin; for (let t = tMin; t <= tMax; t++) if (d[t] < d[best]) best = t; if (d[best] < 0.35) tau = best; }
    if (tau < 0) continue;
    // Oktave zu tief (doppelte Periode)? Bei halber Periode liegt dann ebenfalls ein deutliches Minimum
    for (const div of [2, 3]) {
      const h = Math.round(tau / div);
      if (h < tMin) continue;
      let hm = h; for (let q = Math.max(tMin, h - 2); q <= Math.min(tMax, h + 2); q++) if (d[q] < d[hm]) hm = q;
      if (d[hm] < Math.max(0.3, d[tau] + 0.12)) { tau = hm; break; }
    }
    const a0 = d[tau - 1], b0 = d[tau], c0 = d[tau + 1], den = a0 - 2 * b0 + c0;
    const tt = tau + (den !== 0 ? (0.5 * (a0 - c0)) / den : 0);
    f0[k] = dsr / tt; conf[k] = Math.max(0, 1 - b0);
  }
  // tatsächlicher Rahmenabstand (bei 44,1 kHz nicht genau 10 ms), sonst wandern die Noten über lange Spuren weg
  return { f0: fillGaps(octaveFix(f0)), conf, hop: hop / dsr, off: W / 2 / dsr };
}

/**
 * Kurze Lücken der Erkennung mitten im Ton schließen (bis 40 ms, Tonhöhe davor und danach höchstens 3 HT auseinander).
 * Sonst schaltet die Korrektur dort kurz ab und die Stimme springt hörbar auf die Originaltonhöhe zurück.
 */
function fillGaps(f0: Float32Array): Float32Array {
  const n = f0.length, out = f0.slice();
  for (let k = 1; k < n; k++) {
    if (out[k] > 0 || !(out[k - 1] > 0)) continue;
    let e = k; while (e < n && !(f0[e] > 0) && e - k < 5) e++;
    if (e >= n || !(f0[e] > 0) || e - k > 4) continue;
    const a = midiOf(out[k - 1]), b = midiOf(f0[e]);
    if (Math.abs(a - b) > 3) continue;
    for (let q = k; q < e; q++) out[q] = hzOf(a + ((b - a) * (q - k + 1)) / (e - k + 1));
    k = e;
  }
  return out;
}

/** Oktavsprünge glätten: weicht ein Rahmen um etwa eine Oktave vom Median der Umgebung (±300 ms) ab, eine Oktave zurück */
function octaveFix(f0: Float32Array): Float32Array {
  const n = f0.length, m = Array.from(f0, (f) => (f > 0 ? midiOf(f) : NaN)), out = f0.slice(), R = 30;
  for (let k = 0; k < n; k++) {
    if (Number.isNaN(m[k])) continue;
    const w: number[] = []; for (let q = Math.max(0, k - R); q <= Math.min(n - 1, k + R); q++) if (!Number.isNaN(m[q])) w.push(m[q]);
    if (w.length < 8) continue;
    w.sort((p, q) => p - q);
    const med = w[Math.floor(w.length / 2)], dv = m[k] - med;
    for (const oct of [12, 24, 19]) {
      if (Math.abs(Math.abs(dv) - oct) < 1.6) { out[k] = hzOf(m[k] - Math.sign(dv) * oct); break; }
    }
  }
  return out;
}

export interface DetNote { /** Quell-Sekunden */ s0: number; s1: number; /** erkannte Tonhöhe (Median), MIDI */ m: number }

/** Gesungene Noten: stimmhafte Abschnitte, getrennt an Pausen und an Tonhöhenwechseln (> 0,7 HT, über 90 ms geglättet) */
export function segmentNotes(tr: PitchTrack, origin = 0): DetNote[] {
  const { f0, hop } = tr, n = f0.length, m = new Float32Array(n).fill(NaN);
  for (let k = 0; k < n; k++) if (f0[k] > 0) m[k] = midiOf(f0[k]);
  // einzelne Lücken schließen
  for (let k = 1; k < n - 1; k++) if (Number.isNaN(m[k]) && !Number.isNaN(m[k - 1]) && !Number.isNaN(m[k + 1])) m[k] = (m[k - 1] + m[k + 1]) / 2;
  const sm = new Float32Array(n).fill(NaN);
  for (let k = 0; k < n; k++) { if (Number.isNaN(m[k])) continue; let s = 0, c = 0; for (let q = Math.max(0, k - 4); q <= Math.min(n - 1, k + 4); q++) if (!Number.isNaN(m[q])) { s += m[q]; c++; } sm[k] = s / c; }
  const notes: DetNote[] = [];
  const close = (a: number, b: number) => {
    if ((b - a) * hop < 0.06) return;
    const inner = Array.from(m.slice(a + Math.floor((b - a) * 0.15), b - Math.floor((b - a) * 0.15))).filter((v) => !Number.isNaN(v)).sort((p, q) => p - q);
    if (!inner.length) return;
    notes.push({ s0: origin + tr.off + a * hop, s1: origin + tr.off + b * hop, m: inner[Math.floor(inner.length / 2)] });
  };
  let a = -1;
  for (let k = 0; k <= n; k++) {
    const v = k < n ? sm[k] : NaN;
    if (Number.isNaN(v)) { if (a >= 0) close(a, k); a = -1; continue; }
    if (a < 0) { a = k; continue; }
    // Wechsel: der geglättete Verlauf weicht über 5 Rahmen deutlich vom bisherigen Mittel der Note ab
    const seg = Array.from(sm.slice(a, k)).sort((p, q) => p - q), center = seg[Math.floor(seg.length / 2)];
    if (k - a >= 6 && Math.abs(v - center) > 0.7) {
      let ok = true; for (let q = k; q < Math.min(n, k + 5); q++) if (Number.isNaN(sm[q]) || Math.abs(sm[q] - center) <= 0.7) { ok = false; break; }
      if (ok) { close(a, k); a = k; }
    }
  }
  // kurze Ausreißer (< 100 ms, mehr als 4 HT neben beiden Nachbarn): Übergänge und Konsonanten, keine Noten
  return notes.filter((nt, i) => {
    if (nt.s1 - nt.s0 >= 0.1) return true;
    const nb = [notes[i - 1], notes[i + 1]].filter((x) => x && Math.min(Math.abs(x.s0 - nt.s1), Math.abs(nt.s0 - x.s1)) < 0.08);
    return !nb.length || nb.some((x) => Math.abs(x.m - nt.m) <= 4);
  });
}

/** Tonart schätzen (Krumhansl-Profile, Tonhöhenklassen nach Notenlänge gewichtet), z. B. „F#-Moll“ */
export function estimateKey(notes: DetNote[]): { key: string; score: number } {
  const MAJ = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88], MIN = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
  const NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const h = new Array(12).fill(0);
  for (const n of notes) h[((Math.round(n.m) % 12) + 12) % 12] += n.s1 - n.s0;
  const corr = (prof: number[], r: number) => { const a = h, b = prof.map((_, i) => prof[(i - r + 12) % 12]); const ma = a.reduce((x, y) => x + y) / 12, mb = b.reduce((x, y) => x + y) / 12; let num = 0, da = 0, db = 0; for (let i = 0; i < 12; i++) { num += (a[i] - ma) * (b[i] - mb); da += (a[i] - ma) ** 2; db += (b[i] - mb) ** 2; } return num / Math.sqrt(da * db || 1); };
  let best = { key: 'C', score: -2 };
  for (let r = 0; r < 12; r++) { const a = corr(MAJ, r), b = corr(MIN, r); if (a > best.score) best = { key: `${NAMES[r]}-Dur`, score: a }; if (b > best.score) best = { key: `${NAMES[r]}-Moll`, score: b }; }
  return best;
}

export interface TuneNote extends DetNote { /** Ziel-Tonhöhe, MIDI */ to: number }
export interface TuneOpts { /** Drift innerhalb einer Note glätten, 0–1 */ drift: number; /** Vibrato abschwächen, 0–1 (0 = behalten) */ vibrato: number; /** Übergangszeit zwischen Noten in ms (klein = hart, wie Auto-Tune) */ speed: number; /** lange Noten langsamer ziehen, 0–1 */ humanize?: number }
export const DEFAULT_TUNE: TuneOpts = { drift: 0.5, vibrato: 0, speed: 40 };
/** Voreinstellungen (nach Auto-Tune/Melodyne): natürlich, Standard, harter Effekt (Retune 0, ohne Vibrato und Drift) */
export const TUNE_PRESETS = {
  natural: { label: 'Natürlich', strength: 0.8, drift: 0, vibrato: 0, speed: 60, humanize: 0.6, flex: 70, keep: 25 },
  standard: { label: 'Standard', strength: 1, drift: 0.3, vibrato: 0, speed: 40, humanize: 0.3, flex: 0, keep: 0 },
  hard: { label: 'Hart (Auto-Tune-Effekt)', strength: 1, drift: 1, vibrato: 1, speed: 0, humanize: 0, flex: 0, keep: 0 },
} as const;
/** Versatz des Halbtonrasters in Halbtönen bei anderem Kammerton (z. B. 442 Hz: +0,08) */
export const refOffset = (ref?: number) => (ref && ref > 0 ? 12 * Math.log2(ref / 440) : 0);
/** Median der Tonhöhe zwischen s0 und s1 (Quell-Sekunden), ohne die Ränder; für geteilte Noten */
export function noteMedian(tr: PitchTrack, s0: number, s1: number): number {
  const k0 = Math.round((s0 - tr.off) / tr.hop), k1 = Math.round((s1 - tr.off) / tr.hop), L = k1 - k0, v: number[] = [];
  for (let k = k0 + Math.floor(L * 0.15); k < k1 - Math.floor(L * 0.15); k++) if (tr.f0[k] > 0) v.push(midiOf(tr.f0[k]));
  if (!v.length) for (let k = k0; k < k1; k++) if (tr.f0[k] > 0) v.push(midiOf(tr.f0[k]));
  v.sort((a, b) => a - b);
  return v.length ? v[Math.floor(v.length / 2)] : NaN;
}
/**
 * Ziele für alle Noten. Kurze Noten (unter minDur, z. B. Gleiter und Übergänge im schnellen Flow) bekommen kein eigenes
 * Ziel, sie wandern mit ihren direkten Nachbarn mit (Mittel von deren Korrektur), so bleibt die Form des Übergangs
 * natürlich, wie beim Übergangswerkzeug von Melodyne. Sonst zittert es, wenn jeder Gleiter einzeln einrastet.
 */
export function correctNotes(notes: { s0: number; s1: number; m: number; to: number; off?: boolean }[], key: string | undefined, o: { strength?: number; flex?: number; ref?: number; minDur?: number; keep?: number } = {}): number[] {
  const minDur = o.minDur ?? 0.12, isShort = (n: { s0: number; s1: number }) => n.s1 - n.s0 < minDur;
  const to = notes.map((n) => (n.off ? n.to : targetFor(n.m, key, o)));
  // Intervalle halten: direkt benachbarte (lange) Noten nicht gegeneinander ziehen. Ändert sich das gesungene Intervall
  // um mehr als keep Cent, werden beide Korrekturen aufeinander zu angeglichen (die Melodieführung bleibt)
  if (o.keep && o.keep > 0) {
    const lim = o.keep / 100, idx = notes.map((n, i) => i).filter((i) => !notes[i].off && !isShort(notes[i]));
    for (let pass = 0; pass < 3; pass++) {
      for (let q = 1; q < idx.length; q++) {
        const a = idx[q - 1], b = idx[q];
        if (notes[b].s0 - notes[a].s1 > 0.06) continue;
        const da = to[a] - notes[a].m, db = to[b] - notes[b].m, d = db - da;
        if (Math.abs(d) <= lim + 1e-9) continue;
        const ex = (Math.abs(d) - lim) / 2 * Math.sign(d);
        to[a] += ex; to[b] -= ex;
      }
    }
  }
  const near = (i: number, dir: -1 | 1) => {
    // nächste lange Nachbarnote in Richtung dir, höchstens 150 ms Lücke
    for (let j = i + dir, edge = dir < 0 ? notes[i].s0 : notes[i].s1; j >= 0 && j < notes.length; j += dir) {
      const g = dir < 0 ? edge - notes[j].s1 : notes[j].s0 - edge;
      if (g > 0.15) return null;
      if (!isShort(notes[j]) && !notes[j].off) return to[j] - notes[j].m;
      edge = dir < 0 ? notes[j].s0 : notes[j].s1;
    }
    return null;
  };
  return notes.map((n, i) => {
    if (n.off || !isShort(n)) return to[i];
    const a = near(i, -1), b = near(i, 1), ds = [a, b].filter((x): x is number => x !== null);
    return n.m + (ds.length ? ds.reduce((p, q) => p + q, 0) / ds.length : 0);
  });
}

/** Zielton für eine Note: nächster Halbton bzw. Ton der Tonart (mit Kammerton), Stärke, Flex-Tune (weit weg: unverändert) */
export function targetFor(m: number, key: string | undefined, o: { strength?: number; flex?: number; ref?: number } = {}): number {
  const off = refOffset(o.ref), mm = m - off;
  const best = !key || key === 'chromatic' ? Math.round(mm) : toKey(mm, key, 1);
  if (o.flex && Math.abs(best - mm) * 100 > o.flex) return m;
  return m + (best - mm) * Math.max(0, Math.min(1, o.strength ?? 1));
}
/** Verändert das Tuning den Klang? */
export const tuneActive = (t?: { notes: { m: number; to: number; off?: boolean }[]; drift: number; vibrato: number }) => !!t && t.notes.some((n) => !n.off) && (t.drift > 0.001 || t.vibrato > 0.001 || t.notes.some((n) => !n.off && Math.abs(n.to - n.m) > 0.003));

/**
 * Korrekturkurve in Halbtönen je Rahmen (gleiches Raster wie die Tonhöhe): Notenversatz (to − m, mit Übergangszeit
 * geglättet), minus geglätteter Drift, minus Vibrato-Anteil. Außerhalb der Noten 0.
 */
export function tuneCurve(tr: PitchTrack, notes: TuneNote[], o: TuneOpts, origin = 0): Float32Array {
  const { f0, hop } = tr, n = f0.length, base = new Float32Array(n), det = new Float32Array(n), inNote = new Int32Array(n).fill(-1);
  const o0 = origin + tr.off;
  const win = new Int32Array(n), w0 = Math.max(0, Math.round(o.speed / 1000 / hop / 2)), hz = Math.max(0, Math.min(1, o.humanize ?? 0));
  notes.forEach((nt, i) => {
    if ((nt as TuneNote & { off?: boolean }).off) return;   // Bypass: Note bleibt, wie gesungen
    // Humanize: je länger die Note, desto länger der Übergang (bis zum Vierfachen bei Noten ab ~1 s)
    const wi = Math.round(w0 * (1 + hz * Math.min(3, Math.max(0, (nt.s1 - nt.s0 - 0.25) / 0.25))));
    for (let k = Math.max(0, Math.round((nt.s0 - o0) / hop)); k < Math.min(n, Math.round((nt.s1 - o0) / hop)); k++) { base[k] = nt.to - nt.m; inNote[k] = i; win[k] = wi; }
  });
  // Übergänge: Notenversatz über die Übergangszeit glätten (Fenster je Note, Humanize)
  const cs = new Float64Array(n + 1); for (let k = 0; k < n; k++) cs[k + 1] = cs[k] + base[k];
  const bs = new Float32Array(n);
  for (let k = 0; k < n; k++) {
    let w = win[k]; if (!w) { for (let q = 1; q <= 3 * w0 + 1 && !w; q++) w = win[Math.max(0, k - q)] || win[Math.min(n - 1, k + q)]; w = w || w0; }
    if (!w) { bs[k] = base[k]; continue; }
    const a = Math.max(0, k - w), b = Math.min(n, k + w + 1); bs[k] = (cs[b] - cs[a]) / (2 * w + 1);
  }
  // Drift (langsam, ~150 ms) und Vibrato (Rest) je Note
  const W = 7;
  for (let k = 0; k < n; k++) {
    const i = inNote[k];
    if (i < 0 || !f0[k]) continue;
    const p = midiOf(f0[k]);
    let s = 0, c = 0; for (let q = Math.max(0, k - W); q <= Math.min(n - 1, k + W); q++) if (inNote[q] === i && f0[q] > 0) { s += midiOf(f0[q]); c++; }
    const slow = s / c, drift = slow - notes[i].m, vib = p - slow;
    det[k] = -o.drift * drift - o.vibrato * vib;
  }
  const out = new Float32Array(n);
  for (let k = 0; k < n; k++) out[k] = bs[k] + det[k];
  return out;
}

/** Nächster Ton der Tonart (Dur/Moll aus dem Projekt), mit Stärke 0–1 */
export function toKey(m: number, key: string, strength = 1): number {
  const r = Math.round(m);
  const cand = [r - 2, r - 1, r, r + 1, r + 2].filter((c) => inKey(c, key)).sort((a, b) => Math.abs(a - m) - Math.abs(b - m));
  const best = cand[0] ?? r;
  return m + (best - m) * Math.max(0, Math.min(1, strength));
}
